import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApplicationProductTypeEnum } from '@varo/graphql-schema-types';
import { router } from 'expo-router';
import { MONTHS_IN_YEAR } from 'features/advance/constants/constants';
import { EmploymentInfoSchema } from 'features/advance/screens/registration/EmploymentInfo/EmploymentInfo.schema';
import React, { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import VXLDropdown from 'shared/components/vxl/input/VXLDropdown/VXLDropdown';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import theme from 'shared/styles/theme';

const format = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

interface EmploymentInfoProps {
  employmentTypes: { id: string; name: string }[];
  isVloc?: boolean;
  pdfType: ApplicationProductTypeEnum;
  handleSubmit: (props) => void;
  fetchingStatus: boolean;
  showButtons: boolean;
}

const EmploymentInfoForm = ({
  employmentTypes,
  isVloc = false,
  pdfType,
  handleSubmit,
  fetchingStatus,
  showButtons,
}: EmploymentInfoProps) => {
  const [isChecked, setChecked] = useState(false);
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const { t: translatedText } = useTranslation(isVloc ? 'vloc' : 'advance', {
    keyPrefix: isVloc ? 'applyEmployment' : 'employmentInfo',
  });
  const insets = useSafeAreaInsets();

  const {
    control,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      employmentStatus: '',
      monthlyIncome: '',
      tAndC: false,
    },
    resolver: zodResolver(EmploymentInfoSchema),
  });

  // Check if there is a custom title for the terms and conditions
  // If there is, use that title, otherwise use the default title in ReviewPDF Screen
  const hasTAndCTitle =
    translatedText('termsAndConditions.title') !==
      'employmentInfo.termsAndConditions.title' &&
    translatedText('termsAndConditions.title') !==
      'applyEmployment.termsAndConditions.title';

  const tAndCTitle = hasTAndCTitle
    ? translatedText('termsAndConditions.title')
    : '';

  const viewPdf = () => {
    router.push({
      pathname: '/terms-and-conditions',
      params: {
        agreementType: pdfType,
        title: tAndCTitle.length ? tAndCTitle : undefined,
      },
    });
  };

  const openBottomSheet = () => {
    bottomSheetRef.current?.present();
  };

  const closeBottomSheet = () => {
    bottomSheetRef.current?.close();
  };

  const handleFAQsPress = () => {
    openBottomSheet();
  };

  const { employmentStatus, monthlyIncome, tAndC } = getValues();

  const employmentStatusPressed = usePressableAnalytics({
    analyticsId: 'employmentStatusSelected',
    customAttributes: { employment_status: employmentStatus },
  });

  const monthlyIncomePressed = usePressableAnalytics({
    analyticsId: 'monthlyIncomeTextFieldEntered',
    customAttributes: { amount: monthlyIncome.replace('$', '') },
  });

  const handleContinuePressAnalytics = usePressableAnalytics({
    analyticsId: 'vaEmploymentInfoSubmitted',
    customAttributes: {
      employment_status_selected: employmentStatus,
      income_amount: monthlyIncome.replace('$', ''),
    },
  });

  const submitForm = () => {
    if (isVloc) {
      employmentStatusPressed();
      monthlyIncomePressed();
    } else {
      handleContinuePressAnalytics();
    }
    handleSubmit({ employmentStatus, monthlyIncome, tAndC });
  };

  const employmentOptions = employmentTypes?.map((type) => type.name);

  const dismissToTab = useDismissToRootTab();

  const goBack = () => {
    if (router.canGoBack() && isVloc) {
      router.back();
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.container}>
        <ScrollView contentContainerStyle={styles.scrollViewContent}>
          <TitleLgText numberOfLines={0}>{translatedText('title')}</TitleLgText>
          <BodyBaseText numberOfLines={0}>
            {translatedText('subTitle')}
          </BodyBaseText>
          <Controller
            control={control}
            name="employmentStatus"
            render={({ field: { onChange, value } }) => (
              <VXLDropdown
                labelText={translatedText('employmentStatus')}
                options={employmentOptions}
                value={value}
                errorText={errors.employmentStatus?.message ?? ''}
                testID="employmentStatus"
                onChangeText={(selectedValue) => {
                  onChange(selectedValue);
                }}
                analyticsId="employmentStatusDropDownClicked"
              />
            )}
          />

          <Controller
            control={control}
            name="monthlyIncome"
            render={({ field: { onChange, onBlur, value } }) => {
              const handleChange = (e: string) => {
                const strippedCharacters = e.replace(/\D/g, '');
                const f = format.format(Number(strippedCharacters));
                onChange(f);
                onBlur();
              };
              const strippedCharacters = value.replace(/\D/g, '');
              const annualIncome = format.format(
                Number(strippedCharacters) * MONTHS_IN_YEAR,
              );
              const showEstimatedIncome =
                value.length > 0 && !errors.monthlyIncome;
              const estimatedAnnualIncomeString = `${translatedText(
                'annualIncomeEstimate',
              )} ${annualIncome}`;
              return (
                <>
                  <View style={styles.input}>
                    <VXLTextInput
                      labelText={translatedText('monthlyIncome')}
                      errorText={errors.monthlyIncome?.message ?? ''}
                      keyboardType="phone-pad"
                      returnKeyType="done"
                      value={value}
                      onChangeText={(e: string) => {
                        handleChange(e);
                      }}
                      onBlur={onBlur}
                      analyticsId="monthlyIncomeTextFieldClicked"
                    />
                  </View>
                  {showEstimatedIncome ? (
                    <BodySmText
                      style={styles.estimatedAnnualIncome}
                      numberOfLines={0}
                    >
                      {estimatedAnnualIncomeString}
                    </BodySmText>
                  ) : null}
                </>
              );
            }}
          />
          <View>
            <View style={styles.whatCountsAsIncomeButton}>
              <HyperlinkBaseText
                onPress={handleFAQsPress}
                analyticsId="faqsClicked"
                customAttributes={{
                  faq_name: isVloc ? 'income' : 'what_counts_as_income',
                }}
                numberOfLines={0}
              >
                {translatedText('whatCountsAsIncome')}
              </HyperlinkBaseText>
            </View>

            <Controller
              control={control}
              name="tAndC"
              render={({ field: { onChange, onBlur } }) => (
                <View style={styles.tAndC}>
                  <Checkbox
                    testId={translatedText('checkboxAriaLabel')}
                    checked={isChecked}
                    onSelectedStateChange={() => {
                      onChange(!isChecked);
                      onBlur();
                      setChecked(!isChecked);
                    }}
                    labelText=""
                    ariaLabel={translatedText('checkboxAriaLabel')}
                    analyticsId="termsAndConditionsClicked"
                  />
                  <View style={styles.creditCheckLabel}>
                    <BodyBaseText numberOfLines={0}>
                      {translatedText('agreeAndAcknowledge')}

                      <HyperlinkBaseText
                        analyticsId="viewPdfClicked"
                        onPress={viewPdf}
                        numberOfLines={0}
                      >
                        {translatedText('termsAndConditions')}
                      </HyperlinkBaseText>
                    </BodyBaseText>
                  </View>
                </View>
              )}
            />
          </View>
        </ScrollView>

        {!!employmentTypes && showButtons ? (
          <View>
            {!isVloc ? (
              <BodyXSText style={styles.achText} numberOfLines={0}>
                {translatedText('ifApproveAch')}
              </BodyXSText>
            ) : null}
            <View
              style={[
                styles.buttonContainer,
                { marginBottom: insets.bottom || theme.spacing.m },
              ]}
            >
              <Button
                label={translatedText('buttons.primary')}
                type="PRIMARY"
                onPress={submitForm}
                isDisabled={!isValid}
                isLoading={fetchingStatus}
                analyticsId="vaRegistrationStepCompleted"
                customAttributes={{ step_name: 'employment_info' }}
              />
              <Button
                label={translatedText('buttons.secondary')}
                type="SECONDARY"
                onPress={goBack}
                analyticsId="vaEmploymentInfoExited"
              />
            </View>
          </View>
        ) : null}
      </View>

      <BottomSheet ref={bottomSheetRef}>
        <View style={styles.bottomSheet}>
          <TitleMdText style={styles.bottomSheetTitle} numberOfLines={0}>
            {translatedText('whatCountsAsIncome')}
          </TitleMdText>
          <BodyBaseText style={styles.bottomSheetBody} numberOfLines={0}>
            {translatedText('incomeDescription')}
          </BodyBaseText>
          <View style={styles.bottomSheetButtonContainer}>
            <Button
              label={translatedText('buttons.gotIt')}
              type="PRIMARY"
              onPress={closeBottomSheet}
              analyticsId="gotIt"
            />
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  achText: {
    color: theme.color.text.secondary,
    paddingVertical: theme.spacing.m,
  },
  scrollViewContent: {
    gap: theme.spacing.m,
  },
  bottomSheet: {
    paddingHorizontal: theme.spacing.m,
  },
  bottomSheetBody: {
    marginBottom: theme.spacing.m,
  },
  bottomSheetButtonContainer: {
    paddingVertical: theme.spacing.m,
  },
  bottomSheetTitle: {
    marginBottom: theme.spacing.s,
  },
  buttonContainer: {
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  creditCheckLabel: {
    flexShrink: 1,
    paddingVertical: theme.spacing.s,
  },
  estimatedAnnualIncome: {
    color: theme.color.text.secondary,
  },
  input: {
    ...(isWeb && { marginTop: theme.spacing.m }),
  },
  tAndC: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.spacing.m,
  },
  whatCountsAsIncomeButton: {
    marginVertical: theme.spacing.s,
  },
});

export default EmploymentInfoForm;
